import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { adminAPI, getErrorMessage } from '../../utils/api';
import { formatDate } from '../../utils/helpers';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import AdminTableToolbar from '../../components/admin/AdminTableToolbar';
import AdminPagination from '../../components/admin/AdminPagination';

const STATUS_TONE = {
  active: 'bg-green-500/15 text-green-400 border-green-500/40',
  paused: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/40',
  expired: 'bg-gray-500/15 text-gray-300 border-gray-500/40',
  pending: 'bg-blue-500/15 text-blue-400 border-blue-500/40',
};

const StatusBadge = ({ status }) => (
  <span
    className={[
      'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border',
      STATUS_TONE[status] || 'bg-gray-500/15 text-gray-400 border-gray-500/30',
    ].join(' ')}
  >
    {status || 'unknown'}
  </span>
);

const gbp = (n) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 2,
  }).format(Number(n || 0));

const AdminMiningContracts = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Payout adjustment modal state
  const [payoutModal, setPayoutModal] = useState(null);
  const [payoutAction, setPayoutAction] = useState('credit');
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutReason, setPayoutReason] = useState('');
  const [payoutLoading, setPayoutLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getMiningContracts(
        statusFilter ? { status_filter: statusFilter } : undefined
      );
      setRows(Array.isArray(res.data) ? res.data : []);
      setPage(1);
    } catch (err) {
      toast.error('Failed to load mining contracts');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id, action) => {
    try {
      await adminAPI.miningContractAction(id, action);
      toast.success(`Contract ${action}d`);
      load();
    } catch (err) {
      toast.error('Action failed');
    }
  };

  const openPayoutModal = (contract) => {
    setPayoutModal({ contractId: contract.id, userId: contract.user_id, totalMined: contract.total_mined });
    setPayoutAction('credit');
    setPayoutAmount('');
    setPayoutReason('');
  };

  const closePayoutModal = () => {
    setPayoutModal(null);
    setPayoutAmount('');
    setPayoutReason('');
  };

  const handlePayoutAdjust = async (e) => {
    e.preventDefault();
    if (!payoutModal) return;
    setPayoutLoading(true);
    try {
      await adminAPI.adjustMiningPayout(payoutModal.contractId, {
        action: payoutAction,
        amount: parseFloat(payoutAmount),
        reason: payoutReason,
      });
      toast.success(`Mining payout ${payoutAction === 'credit' ? 'credited' : 'debited'} successfully`);
      closePayoutModal();
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Payout adjustment failed'));
    } finally {
      setPayoutLoading(false);
    }
  };

  const payoutAmountNum = parseFloat(payoutAmount) || 0;
  const payoutCanSubmit = payoutAmountNum > 0 && payoutReason.trim() !== '' &&
    !(payoutAction === 'debit' && payoutAmountNum > (payoutModal?.totalMined || 0));

  const term = search.trim().toLowerCase();
  const filtered = term
    ? rows.filter(
        (r) =>
          (r.user_email || '').toLowerCase().includes(term) ||
          (r.user_full_name || '').toLowerCase().includes(term) ||
          (r.order_id || '').toLowerCase().includes(term)
      )
    : rows;
  const total = filtered.length;
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const filterOptions = [
    { value: 'active', label: 'Active' },
    { value: 'paused', label: 'Paused' },
    { value: 'expired', label: 'Expired' },
    { value: 'pending', label: 'Pending' },
  ];

  if (loading && rows.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="admin-mining-contracts">
      <h1 className="text-2xl sm:text-4xl font-bold text-foreground">
        Mining Contracts
      </h1>

      <AdminTableToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by user email, name, or order ID..."
        filterValue={statusFilter}
        onFilterChange={(v) => {
          setStatusFilter(v);
          setPage(1);
        }}
        filterOptions={filterOptions}
        filterPlaceholder="All statuses"
      />

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Package</TableHead>
                <TableHead className="text-right">Value</TableHead>
                <TableHead className="text-right">Hash Power</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Total Mined</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paged.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10 text-muted-foreground">
                    No mining contracts found.
                  </TableCell>
                </TableRow>
              ) : (
                paged.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>
                      <div className="font-medium text-foreground">
                        {c.user_email || '—'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {c.user_full_name || ''}
                      </div>
                    </TableCell>
                    <TableCell className="capitalize">{c.package_name}</TableCell>
                    <TableCell className="text-right">{gbp(c.package_value)}</TableCell>
                    <TableCell className="text-right">
                      {Number(c.hash_power_ths || 0).toFixed(2)} TH/s
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={c.status} />
                    </TableCell>
                    <TableCell className="text-right text-green-500 font-medium">
                      {gbp(c.total_mined)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDate(c.created_at)}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button
                        size="sm"
                        variant="outline"
                        className="mr-1 border-blue-200 text-blue-600 hover:bg-blue-50"
                        onClick={() => openPayoutModal(c)}
                      >
                        Adjust Payout
                      </Button>
                      {c.status !== 'paused' && c.status !== 'expired' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="mr-1"
                          onClick={() => act(c.id, 'pause')}
                        >
                          Pause
                        </Button>
                      )}
                      {c.status === 'paused' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="mr-1"
                          onClick={() => act(c.id, 'resume')}
                        >
                          Resume
                        </Button>
                      )}
                      {c.status !== 'expired' && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => act(c.id, 'expire')}
                        >
                          Expire
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <AdminPagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
      />

      {/* Payout Adjustment Modal */}
      <Dialog open={!!payoutModal} onOpenChange={(open) => { if (!open) closePayoutModal(); }}>
        <DialogContent className="sm:max-w-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle>Adjust Mining Payout</DialogTitle>
          </DialogHeader>

          <form onSubmit={handlePayoutAdjust} className="space-y-4 pt-1">
            {/* Current total mined */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">Current Total Mined</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">
                {(payoutModal?.totalMined || 0).toFixed(8)} BTC
              </p>
            </div>

            {/* Credit / Debit toggle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Action</label>
              <div className="flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
                {['credit', 'debit'].map((a) => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => setPayoutAction(a)}
                    className={`flex-1 py-2 text-sm font-medium capitalize transition-colors ${
                      payoutAction === a
                        ? a === 'credit'
                          ? 'bg-green-600 text-white'
                          : 'bg-red-600 text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>

            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Amount (BTC)
              </label>
              <input
                type="number"
                min="0.00000001"
                step="any"
                placeholder="Enter BTC amount"
                value={payoutAmount}
                onChange={(e) => setPayoutAmount(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {payoutAction === 'debit' && payoutAmountNum > (payoutModal?.totalMined || 0) && (
                <p className="text-xs mt-1 text-red-600 font-medium">⚠ Exceeds total mined balance</p>
              )}
            </div>

            {/* Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                placeholder="e.g. Manual correction, Bonus reward..."
                rows={2}
                value={payoutReason}
                onChange={(e) => setPayoutReason(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={closePayoutModal}
                className="flex-1 py-3 border border-gray-300 dark:border-gray-600 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!payoutCanSubmit || payoutLoading}
                className={`flex-1 py-3 rounded-xl text-white font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  payoutAction === 'credit' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {payoutLoading ? 'Processing...' : `${payoutAction === 'credit' ? 'Credit' : 'Debit'} ${payoutAmount || '0'} BTC`}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMiningContracts;
